import React, { useEffect, useState, useRef } from "react";
import { getProjectMessages, sendMessages } from "../../Services/MessageServices";
import toast from "react-hot-toast";
import { TOAST_MESSAGES } from "./constants";
import { FaTimesCircle } from "react-icons/fa";
import { IoMdSend } from "react-icons/io";
import { formatTimeAMPM } from "../../Utils/timeUtils";

const ChatWindow = ({ projectId, onClose, projectName }) => {
    const [messages, setMessages] = useState([]);
    const [loading, setLoading] = useState(false);
    const [newMessage, setNewMessage] = useState("");
    const chatContainerRef = useRef(null);
    const [pagination, setPagination] = useState({ limit: 10, offset: 0 });
    const [hasMore, setHasMore] = useState(true);

    const PAGE_SIZE = 10;

    const sortMessages = (msgs) =>
        [...msgs].sort((a, b) => new Date(a.created) - new Date(b.created));

    const fetchMessages = async (prepend = false) => {
        if (!hasMore) return;
        setLoading(true);
        try {
            const res = await getProjectMessages(projectId, pagination);
            const fetchedMessages = res.data.results || [];

            if (fetchedMessages.length < pagination.limit) setHasMore(false);

            if (prepend) {
                const scrollHeightBefore = chatContainerRef.current.scrollHeight;
                setMessages((prev) => sortMessages([...fetchedMessages, ...prev]));
                setTimeout(() => {
                    if (chatContainerRef.current) {
                        chatContainerRef.current.scrollTop =
                            chatContainerRef.current.scrollHeight - scrollHeightBefore;
                    }
                }, 50);
            } else {
                setMessages((prev) => sortMessages([...fetchedMessages, ...prev]));
                setTimeout(() => {
                    if (chatContainerRef.current) {
                        chatContainerRef.current.scrollTop =
                            chatContainerRef.current.scrollHeight;
                    }
                }, 100);
            }

            setPagination((prev) => ({
                ...prev,
                offset: prev.offset + PAGE_SIZE,
            }));
        } catch {
            toast.error(TOAST_MESSAGES.MESSAGES_LOAD_FAIL);
        }
        setLoading(false);
    };

    useEffect(() => {
        setMessages([]);
        setPagination({ limit: PAGE_SIZE, offset: 0 });
        setHasMore(true);
        fetchMessages();
    }, [projectId]);

    const handleScroll = () => {
        if (!chatContainerRef.current || loading || !hasMore) return;
        if (chatContainerRef.current.scrollTop === 0) {
            fetchMessages(true);
        }
    };

    const handleSend = async () => {
        if (!newMessage.trim()) return;
        try {
            const res = await sendMessages(projectId, { message: newMessage });
            setMessages((prev) => sortMessages([...prev, res.data]));
            setNewMessage("");
            setTimeout(() => {
                if (chatContainerRef.current) {
                    chatContainerRef.current.scrollTop =
                        chatContainerRef.current.scrollHeight;
                }
            }, 50);
        } catch {
            toast.error(TOAST_MESSAGES.MESSAGE_SEND_ERROR);
        }
    };

    return (
      <div className="absolute bg-white rounded-lg shadow-xl flex flex-col z-50 right-[4vw] bottom-[8vh] w-[25vw] min-w-[300px] max-w-[400px] h-[60vh]">
        {/* Header */}
        <div className="flex justify-between items-center p-3 bg-primary text-white rounded-t-lg">
          <h3 className="text-sm font-semibold">
            Project Chat ({projectName})
          </h3>
          <button onClick={onClose} className="text-sm">
            <FaTimesCircle size={20} />
          </button>
        </div>

        {/* Messages */}
        <div
          ref={chatContainerRef}
          onScroll={handleScroll}
          className="flex-1 overflow-y-auto p-3 space-y-2 text-sm"
        >
          {loading && (
            <div className="flex justify-center py-2">
              <div className="animate-spin h-5 w-5 border-2 border-blue-500 border-t-transparent rounded-full"></div>
            </div>
          )}

          {!loading && messages.length === 0 && (
            <div className="text-center text-gray-500 py-4">
              {TOAST_MESSAGES.NO_MESSAGES}
            </div>
          )}

          {messages.map((msg) => (
            <div key={msg.id} className="bg-gray-100 text-left p-2 rounded-md">
              {/* Sender Name with fixed width */}
              <div className="flex justify-between">
                <p
                  className="font-semibold max-w-[180px] break-words text-gray-800"
                  title={msg.sender_name}
                >
                  {msg.sender_name}
                </p>
                <p className="text-xs text-gray-500 mt-1 mb-1">
                  {msg.created
                    ? new Date(msg.created).toLocaleDateString() +
                      " " +
                      formatTimeAMPM(new Date(msg.created).toLocaleTimeString())
                    : "--"}
                </p>
              </div>

              {/* Chat Message with fixed width */}
              <p
                className="max-w-[220px] break-words text-gray-700"
                title={msg.message}
              >
                {msg.message}
              </p>
            </div>
          ))}
        </div>

        {/* Input */}
        <div className="p-2 flex border-t">
          <input
            type="text"
            value={newMessage}
            onChange={(e) => setNewMessage(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter") {
                e.preventDefault();
                handleSend();
              }
            }}
            placeholder="Type a message..."
            className="flex-1 px-2 py-1 border rounded-lg"
          />
          <button
            onClick={handleSend}
            className="ml-2 bg-primary text-white px-3 rounded-lg"
          >
            <IoMdSend />
          </button>
        </div>
      </div>
    );
};

export default ChatWindow;
